import React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import {
    datasetLoadingSelector,
    entitySelector,
    createFetchPaginatedDataset
} from '@libs/datasets';

import { AppliedLeavesShared } from '../shared/LeaveComponents';
import { EMPLOYEE_APPLIED_LEAVES_FOR_USERS } from './redux.datasets';
import { DOMAIN_NAME, ROWS_PER_PAGE } from './common';
import LeaveRequestDetails from './LeaveRequestDetails';

const propTypes = {};

const defaultProps = {
    entity: EMPLOYEE_APPLIED_LEAVES_FOR_USERS,
    domain: DOMAIN_NAME,
    loading: true
};

const TABLE_DATA = {
    'employeeEmail': 'Employee Email',
    'categoryName': 'Leave Type',
    'totalNumberOfDays': 'Number Of Days',
    'startDate': 'Start Date',
    'endDate': 'End Date',
    'status': 'Status'
};

const DETAILS_DATA = {
    'employeeEmail': {
        label: 'Employee Email',
        type: 'string'
    },
    'status': {
        label: 'Status',
        type: 'string'
    },
    'category': {
        label: 'Category',
        type: 'object',
        key: 'name'
    },
    'totalNumberOfDays': {
        label: 'Number Of Days',
        type: 'string'
    },
    'startDate': {
        label: 'Start Date',
        type: 'date'
    },
    'endDate': {
        label: 'End Date',
        type: 'date'
    },
    'employeesToNotify': {
        label: 'Employees To Notify',
        type: 'array'
    },
    'isEmergency': {
        label: 'Is Emergency',
        type: 'boolean'
    },
    'createdAt': {
        label: 'Created date',
        type: 'date'
    },
    'updatedAt': {
        label: 'Updated date',
        type: 'date'
    },
    'updatedBy': {
        label: 'Updated By',
        type: 'string'
    },
    'requiredDocuments': {
        label: 'Documents',
        type: 'file'
    },
    'reason': {
        label: 'Reason',
        type: 'string'
    },
    'comment': {
        label: 'Admin Comment',
        type: 'string'
    }
};

const totalRowsSelector = createCachedSelector(entitySelector, (resp) =>
    resp.total ? parseInt(resp.total) : 0
)((domain, entity) => `@data-${domain}-${entity}`);

const dataSelector = createSelector(
    (state) =>
        entitySelector(state, DOMAIN_NAME, EMPLOYEE_APPLIED_LEAVES_FOR_USERS),
    (resp) => (resp && resp.data ? resp.data : [])
);

const mapState = (state, { domain, entity }) => ({
    data: dataSelector(state, domain, entity),
    loading: datasetLoadingSelector(state, domain, entity),
    totalRows: totalRowsSelector(state, domain, entity)
});

const mapDispatch = (dispatch, { entity }) => ({
    fetchDataset: ({
        offset = '',
        limit = ROWS_PER_PAGE,
        shouldAppend = false,
        filter
    }) =>
        dispatch(
            createFetchPaginatedDataset({
                entity: entity,
                domain: DOMAIN_NAME
            })({
                limit,
                offset,
                shouldAppend,
                'sort-descending': true,
                filterKey: filter
            })
        )
});

const AppliedLeaves = connect(
    mapState,
    mapDispatch
)(({ fetchDataset, data, totalRows = 0, year, ...props }) => {
    const { state: { email } } = useLocation();

    const getYearDateRange = React.useCallback((selectedYear) => {
        const startDate = dayjs(`${selectedYear}-01-01`).startOf('year').toISOString();
        const endDate = dayjs(`${selectedYear}-12-31`).endOf('year').toISOString();
        return { startDate, endDate };
    }, []);

    // Custom fetch function that uses filter instead of direct from/to params
    const customFetchDataset = React.useCallback(({ offset = '', from, to }) => {
        fetchDataset({
            offset,
            filter: {
                searchTerm: email,
                'leaveDate': {
                    start: from,
                    end: to
                }
            }
        });
    }, [fetchDataset, email]);

    return (
        <AppliedLeavesShared
            title={`All Leaves of ${email}`}
            tableData={TABLE_DATA}
            detailsData={DETAILS_DATA}
            fetchDataset={customFetchDataset}
            data={data}
            totalRows={totalRows}
            year={year}
            showCancelAction={false}
            paginationConfig={{
                rowsPerPage: ROWS_PER_PAGE
            }}
            LeaveDetailsComponent={LeaveRequestDetails}
            selfContainedDialog={true}
            getYearDateRange={getYearDateRange}
            usePaginationVariant={false}
        />
    );
});

AppliedLeaves.propTypes = propTypes;
AppliedLeaves.defaultProps = defaultProps;

export default AppliedLeaves;
